import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

const CATEGORY_COLOR = {
  whisky: "#8b5a2b", rum: "#a67034", tequila: "#b8a663", mezcal: "#7a5d3a",
  vodka: "#9ec0d8", liqueurs: "#b5667d", agave: "#a4b35a", aperitifs: "#c75a3a",
  wine: "#7a2434", beer: "#c79133", sake: "#d4d2c4", vermouth: "#8b3b3b", amaro: "#5e2a2a",
};

export default function SpiritsItem() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/beverages/${id}`)
      .then((r) => setB(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="px-5 md:px-12 py-20 max-w-4xl mx-auto text-white/40">Loading…</div>
    );
  }
  if (notFound || !b) return <Navigate to="/spirits" replace />;

  const color = CATEGORY_COLOR[b.category] || "#888";

  return (
    <div className="bg-[#0E0F0C] text-white min-h-screen" data-testid={`spirits-item-${b.id}`}>
      {/* NOT_AVAILABLE editorial banner mirroring gin behaviour */}
      {!b.is_available && (
        <div className="px-5 md:px-12 pt-6" data-testid="spirits-not-available-banner">
          <div className="max-w-5xl mx-auto bg-[#15171A] border border-white/10 px-5 py-4 text-center">
            <p className="overline text-white/60">Currently not available at BENO · We'll restock soon</p>
          </div>
        </div>
      )}

      <section className="px-5 md:px-12 py-10 md:py-14 max-w-5xl mx-auto">
        <Link to={`/spirits/${b.category}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59] transition-colors mb-6" data-testid="back-category">
          <ArrowLeft size={14} /> All {b.category}
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-3 h-3 rounded-full" style={{ background: color }} />
          <p className="overline text-white/55 capitalize">{b.category}{b.subcategory ? ` · ${b.subcategory}` : ""}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <p className="overline text-white/40 mb-2">{b.brand}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="spirits-item-name">{b.display_name}</h1>
            {b.notes && <p className="text-white/70 mt-5 leading-relaxed max-w-2xl whitespace-pre-wrap">{b.notes}</p>}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-8">
              {b.country && <Fact label="Country" value={b.country} />}
              {b.region && <Fact label="Region" value={b.region} />}
              {b.abv && <Fact label="ABV" value={`${b.abv}%`} />}
              {b.pricing_mode !== "BOTTLE_ONLY" && (b.price_30ml || b.price_60ml) && (
                <Fact label="Pour sizes" value={[b.price_30ml ? "30 ml" : null, b.price_60ml ? "60 ml" : null].filter(Boolean).join(" · ") || `${b.standard_pour_ml || 30} ml`} />
              )}
              {b.pricing_mode === "BOTTLE_ONLY" && <Fact label="Sold as" value="Full bottle" />}
              {b.subcategory && <Fact label="Style" value={b.subcategory} />}
            </div>

            {(b.style_tags?.length || b.flavour_tags?.length) ? (
              <div className="mt-8 space-y-3" data-testid="spirits-item-tags">
                {b.style_tags?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="overline text-white/40 mr-2">Style</span>
                    {b.style_tags.map((t) => <Pill key={t}>{t}</Pill>)}
                  </div>
                )}
                {b.flavour_tags?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="overline text-white/40 mr-2">Flavour</span>
                    {b.flavour_tags.map((t) => <Pill key={t}>{t}</Pill>)}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="md:col-span-5">
            <div className="bg-[#15171A] border border-white/10 p-6" data-testid="spirits-pricing-block">
              {b.pricing_mode === "BOTTLE_ONLY" ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="overline text-[#C17F59]">Bottle only</p>
                    <span className="text-[9px] uppercase tracking-[0.25em] text-[#C17F59] border border-[#C17F59]/40 px-2 py-1" data-testid="bottle-only-badge">
                      Cellar pick
                    </span>
                  </div>
                  <p className="font-display text-4xl" data-testid="spirits-bottle-price">₹{fmt(b.price_by_bottle)}</p>
                  <p className="overline text-white/40 mt-1">per bottle · 750 ml</p>
                  <p className="text-xs text-white/55 mt-3 leading-relaxed">
                    This bottle is curated for celebration, sharing, or cellaring — not poured by the glass tonight.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="overline text-[#C17F59]">Tonight's pour</p>
                    <span className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 border ${
                      b.is_available ? "border-[#C17F59]/40 text-[#C17F59]" : "border-white/15 text-white/40"
                    }`} data-testid="spirits-availability-chip">
                      {b.is_available ? "Available" : "Not on menu"}
                    </span>
                  </div>

                  <PourLine
                    label="30 ml"
                    price={b.price_30ml}
                    fallback={!b.price_60ml ? (b.price_by_glass || b.price) : null}
                    testid="spirits-price-30ml"
                  />
                  <PourLine
                    label="60 ml"
                    price={b.price_60ml}
                    testid="spirits-price-60ml"
                  />

                  {b.pricing_mode === "BOTH" && b.price_by_bottle && (
                    <div className="pt-4 mt-3 border-t border-white/8 flex items-center justify-between" data-testid="spirits-bottle-block">
                      <div>
                        <p className="overline text-white/40 mb-1">By the bottle</p>
                        <p className="font-display text-2xl" data-testid="spirits-bottle-price">₹{fmt(b.price_by_bottle)}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">750 ml</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <Link to="/spirits" className="block mt-4 text-xs uppercase tracking-[0.25em] text-white/45 hover:text-[#C17F59] transition-colors text-right" data-testid="back-spirits-home">
              Browse other categories →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="border-l-2 border-white/10 pl-3">
      <p className="overline text-white/40">{label}</p>
      <p className="font-display text-lg mt-1 capitalize">{value}</p>
    </div>
  );
}

function Pill({ children }) {
  return <span className="text-[10px] uppercase tracking-[0.2em] text-white/70 border border-white/15 px-2 py-1">{children}</span>;
}

function fmt(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-IN");
}

function PourLine({ label, price, fallback, testid }) {
  const value = price != null && price !== "" ? price : fallback;
  if (value == null || value === "") return null;
  return (
    <div className="flex items-baseline justify-between py-2" data-testid={testid}>
      <p className="overline text-white/50">{label}</p>
      <p className="font-display text-3xl tabular-nums">₹{fmt(value)}</p>
    </div>
  );
}
